import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    FaTh, FaUser, FaCalendarAlt, FaComments, FaGraduationCap,
    FaChartBar, FaCog, FaFile, FaSignOutAlt, FaBars, FaTimes,
    FaFileAlt, FaBuilding, FaHeart, FaUserShield, FaBell
} from 'react-icons/fa';
import { useState } from 'react';

const DashboardSidebar = ({ tabs, activeTab, onTabChange, isOpen, onToggle, badgeCounts = {} }) => {
    const { user, logout } = useAuth();
    const location = useLocation();

    // Define sidebar navigation items based on user role
    const getSidebarItems = () => {
        if (!user) return [];

        const role = user.role?.toLowerCase();
        
        const commonItems = [
            { id: 'dashboard', label: 'Dashboard', icon: FaTh, path: '/dashboard' }
        ];

        if (role === 'student_manager') {
            const applicationsCount = badgeCounts.applications || 0;
            return [
                { id: 'dashboard', label: 'Overview', icon: FaTh },
                { id: 'applications', label: 'Applications', icon: FaFileAlt, badge: applicationsCount > 0 ? applicationsCount : null },
                { id: 'interviews', label: 'Interviews', icon: FaCalendarAlt },
                { id: 'communication', label: 'Communication', icon: FaComments },
                { id: 'alumni', label: 'Alumni', icon: FaGraduationCap },
                { id: 'reports', label: 'Reports', icon: FaChartBar },
                { id: 'settings', label: 'Settings', icon: FaCog },
                { id: 'audit', label: 'Audit Logs', icon: FaFile }
            ];
        }

        if (role === 'admin') {
            return [
                { id: 'dashboard', label: 'Overview', icon: FaTh },
                { id: 'users', label: 'Users', icon: FaUser },
                { id: 'sessions', label: 'Sessions', icon: FaCalendarAlt },
                { id: 'employers', label: 'Employers', icon: FaBuilding },
                { id: 'applications', label: 'Applications', icon: FaFileAlt },
                { id: 'donor-requests', label: 'Donors', icon: FaHeart },
                { id: 'events', label: 'News & Events', icon: FaBell },
                { id: 'reports', label: 'Reports', icon: FaChartBar },
                { id: 'settings', label: 'Settings', icon: FaCog }
            ];
        }

        if (role === 'applicant') {
            return [
                { id: 'dashboard', label: 'My Dashboard', icon: FaTh },
                { id: 'applications', label: 'My Applications', icon: FaFileAlt },
                { id: 'announcements', label: 'Announcements', icon: FaComments },
                { id: 'sessions', label: 'Sessions', icon: FaCalendarAlt },
                { id: 'jobs', label: 'Job Board', icon: FaBuilding },
                { id: 'messages', label: 'Messages', icon: FaComments }
            ];
        }

        return commonItems;
    };

    const sidebarItems = getSidebarItems();

    const handleTabClick = (itemId) => {
        if (onTabChange) {
            onTabChange(itemId);
        }
    };

    const handleLogout = () => {
        logout();
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed left-0 right-0 bottom-0 top-20 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={onToggle}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed top-20 left-0 h-[calc(100vh-80px)] bg-white border-r border-gray-200 z-40 transform transition-transform duration-300 ease-in-out w-64 flex flex-col ${
                    isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
                }`}
            >
                {/* Sidebar Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                            <FaUserShield className="text-white text-xl" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Dashboard</h2>
                            <p className="text-xs text-gray-500 capitalize">{user?.role || 'User'}</p>
                        </div>
                    </div>
                    <button
                        onClick={onToggle}
                        className="lg:hidden text-gray-500 hover:text-gray-700"
                    >
                        <FaTimes className="text-xl" />
                    </button>
                </div>

                {/* Navigation Items */}
                <nav className="flex-1 overflow-y-auto py-4">
                    <div className="px-3 space-y-1">
                        {sidebarItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = activeTab === item.id;
                            
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleTabClick(item.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                                        isActive
                                            ? 'bg-blue-50 text-blue-600 font-semibold'
                                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                >
                                    <Icon className={`text-lg ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                                    <span className="flex-1 text-left">{item.label}</span>
                                    {item.badge !== null && item.badge !== undefined && (
                                        <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-semibold min-w-[20px] text-center">
                                            {typeof item.badge === 'number' ? item.badge.toLocaleString() : item.badge}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </nav>

                {/* User Profile Section */}
                <div className="border-t border-gray-200 p-4">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            {user?.email ? (
                                <span className="text-gray-700 font-semibold text-sm">
                                    {user.email.charAt(0).toUpperCase()}
                                </span>
                            ) : (
                                <FaUser className="text-gray-500" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                                {user?.first_name && user?.last_name
                                    ? `${user.first_name} ${user.last_name}`
                                    : user?.email || 'User'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                        <FaSignOutAlt />
                        <span>Logout</span>
                    </button>
                </div>
            </aside>
        </>
    );
};

export default DashboardSidebar;
